import { useMemo, useState } from 'react';

import type { PlainDate, ReviewTimeEntryInput, TimeEntryDto } from '@botguide/shared';
import { formatWeekLabel, reviewTimeEntrySchema, weekStartOf } from '@botguide/shared';

import {
  Badge,
  Button,
  CONTROL_CLASS,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  ErrorState,
  Icon,
  LoadingScreen,
  PageHeader,
  Stat,
} from '@/components';
import { cn } from '@/lib/cn';
import { formatDayShort, formatHours } from '@/lib/format';
import { useReviewTimeEntry, useTimeEntries } from '@/lib/queries';
import { useCan } from '@/lib/session';

/*
 * ---------------------------------------------------------------------------
 * Hours review. Owner only.
 * ---------------------------------------------------------------------------
 *
 * Every pending entry, grouped by the person who logged it and the week they
 * logged it in, because that is the unit the decision is actually made in: you
 * approve somebody's week, not somebody's Tuesday.
 *
 * ## Billable minutes are the point of this screen
 *
 * `billableMinutes` may differ from what was logged. That gap - between what
 * Cody worked and what Bailey is billed - is a judgement made deliberately,
 * so it is shown as a figure and as a running per-week total rather than
 * hidden inside a form field nobody looks at twice.
 *
 * The API refuses billable minutes on a rejection (they would violate
 * `time_entries_billable_requires_approval`), so a reject always sends null.
 */

/** Billable minutes step in quarter hours to match how time is logged. */
const MINUTE_STEP = 15;
const BILLABLE_MIN = 0;
const BILLABLE_MAX = 960;

interface Draft {
  billableMinutes: number;
  reviewNote: string;
}

interface Group {
  key: string;
  userId: string;
  userName: string;
  weekStart: PlainDate;
  entries: TimeEntryDto[];
}

export default function HoursReview() {
  const pending = useTimeEntries({ status: 'pending' });
  const review = useReviewTimeEntry();
  const canApprove = useCan('approve', 'timeEntry');

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  /** The group key whose bulk approval is running, so only it shows busy. */
  const [bulkKey, setBulkKey] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const groups = useMemo(() => groupByPersonAndWeek(pending.data ?? []), [pending.data]);

  const draftFor = (entry: TimeEntryDto): Draft =>
    drafts[entry.id] ?? { billableMinutes: entry.minutes, reviewNote: '' };

  const patchDraft = (entry: TimeEntryDto, patch: Partial<Draft>) =>
    setDrafts((current) => ({ ...current, [entry.id]: { ...draftFor(entry), ...patch } }));

  function submit(entry: TimeEntryDto, status: 'approved' | 'rejected') {
    const draft = draftFor(entry);
    const parsed = reviewTimeEntrySchema.safeParse({
      status,
      // Rejected hours carry no billable figure - the API and the database
      // both refuse one, so it is cleared here rather than sent and bounced.
      billableMinutes: status === 'approved' ? draft.billableMinutes : null,
      reviewNote: draft.reviewNote,
    });

    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'That review could not be submitted.');
      return;
    }

    setFormError(null);
    review.mutate({ id: entry.id, review: parsed.data satisfies ReviewTimeEntryInput });
  }

  async function approveWeek(group: Group) {
    setBulkKey(group.key);
    setFormError(null);
    try {
      // Sequential on purpose: each approval writes a row and invalidates the
      // same caches, and a failure part-way should stop rather than race on.
      for (const entry of group.entries) {
        const draft = draftFor(entry);
        const parsed = reviewTimeEntrySchema.safeParse({
          status: 'approved',
          billableMinutes: draft.billableMinutes,
          reviewNote: draft.reviewNote,
        });
        if (!parsed.success) {
          setFormError(parsed.error.issues[0]?.message ?? 'That review could not be submitted.');
          return;
        }
        await review.mutateAsync({ id: entry.id, review: parsed.data });
      }
    } catch {
      // The mutation's own error is rendered from `review.error`; swallowing it
      // here only stops the unhandled rejection.
    } finally {
      setBulkKey(null);
    }
  }

  if (pending.isPending) return <LoadingScreen label="Loading hours awaiting review" />;

  if (pending.isError) {
    return (
      <>
        <PageHeader title="Review hours" />
        <ErrorState error={pending.error} onRetry={() => void pending.refetch()} />
      </>
    );
  }

  const across = totalsFor(pending.data, drafts);

  return (
    <>
      <PageHeader
        title="Review hours"
        description="Approve or reject logged time, and set what the client is billed for it."
      />

      {pending.data.length === 0 ? (
        <EmptyState
          title="Nothing is waiting on you"
          icon="check"
          description="Every logged hour has been reviewed. New entries land here as soon as they are submitted."
        />
      ) : (
        <>
          <Card padding="lg" className="rise mb-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-6 lg:grid-cols-4">
              <Stat label="Awaiting review" value={pending.data.length} hint="entries" />
              <Stat label="People" value={groups.length} hint="person-weeks" />
              <Stat label="Logged" value={formatHours(across.logged)} hint="as submitted" />
              <Stat
                label="Billable"
                value={formatHours(across.billable)}
                hint={deltaHint(across.billable - across.logged)}
                tone={across.billable === across.logged ? undefined : 'var(--warn)'}
              />
            </div>
          </Card>

          {formError ? <ErrorNote className="mb-4" error={new Error(formError)} /> : null}
          {review.isError ? <ErrorNote className="mb-4" error={review.error} /> : null}

          <div className="flex flex-col gap-6">
            {groups.map((group, index) => (
              <GroupCard
                key={group.key}
                group={group}
                index={index}
                canApprove={canApprove}
                bulkBusy={bulkKey === group.key}
                busyEntryId={review.isPending ? (review.variables?.id ?? null) : null}
                draftFor={draftFor}
                patchDraft={patchDraft}
                onDecide={submit}
                onApproveWeek={() => void approveWeek(group)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* A person's week                                                            */
/* -------------------------------------------------------------------------- */

function GroupCard({
  group,
  index,
  canApprove,
  bulkBusy,
  busyEntryId,
  draftFor,
  patchDraft,
  onDecide,
  onApproveWeek,
}: {
  group: Group;
  index: number;
  canApprove: boolean;
  bulkBusy: boolean;
  busyEntryId: string | null;
  draftFor: (entry: TimeEntryDto) => Draft;
  patchDraft: (entry: TimeEntryDto, patch: Partial<Draft>) => void;
  onDecide: (entry: TimeEntryDto, status: 'approved' | 'rejected') => void;
  onApproveWeek: () => void;
}) {
  const logged = group.entries.reduce((sum, entry) => sum + entry.minutes, 0);
  const billable = group.entries.reduce((sum, entry) => sum + draftFor(entry).billableMinutes, 0);
  const delta = billable - logged;

  return (
    <Card
      padding="lg"
      className="rise"
      style={{ animationDelay: `${Math.min(index, 6) * 70}ms` }}
    >
      <CardHeader
        title={group.userName}
        description={`Week of ${formatWeekLabel(group.weekStart)} · ${group.entries.length} ${
          group.entries.length === 1 ? 'entry' : 'entries'
        }`}
        actions={
          canApprove ? (
            <Button
              variant="primary"
              size="sm"
              iconLeft="check"
              loading={bulkBusy}
              onClick={onApproveWeek}
            >
              Approve the week
            </Button>
          ) : null
        }
      />

      <ul className="mt-4 flex flex-col divide-y divide-line border-y border-line">
        {group.entries.map((entry) => (
          <li key={entry.id}>
            <ReviewRow
              entry={entry}
              draft={draftFor(entry)}
              canApprove={canApprove}
              busy={busyEntryId === entry.id || bulkBusy}
              onChange={(patch) => patchDraft(entry, patch)}
              onDecide={(status) => onDecide(entry, status)}
            />
          </li>
        ))}
      </ul>

      <footer className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[0.8125rem]">
        <p className="text-muted">
          Logged <span className="font-mono font-medium text-ink tnum">{formatHours(logged)}</span>
          <span className="mx-2 text-subtle">→</span>
          Billable{' '}
          <span className="font-mono font-medium text-ink tnum">{formatHours(billable)}</span>
        </p>
        {delta === 0 ? (
          <Badge tone="neutral" size="sm">
            Billed as logged
          </Badge>
        ) : (
          <Badge tone="warn" size="sm" icon="info">
            {deltaHint(delta)}
          </Badge>
        )}
      </footer>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* One entry                                                                  */
/* -------------------------------------------------------------------------- */

function ReviewRow({
  entry,
  draft,
  canApprove,
  busy,
  onChange,
  onDecide,
}: {
  entry: TimeEntryDto;
  draft: Draft;
  canApprove: boolean;
  busy: boolean;
  onChange: (patch: Partial<Draft>) => void;
  onDecide: (status: 'approved' | 'rejected') => void;
}) {
  const diverged = draft.billableMinutes !== entry.minutes;

  const step = (delta: number) =>
    onChange({
      billableMinutes: Math.min(
        BILLABLE_MAX,
        Math.max(BILLABLE_MIN, draft.billableMinutes + delta),
      ),
    });

  return (
    <div className="grid gap-4 py-4 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-[0.8125rem] text-subtle tnum">
            {formatDayShort(entry.workDate)}
          </span>
          <p className="min-w-0 font-medium">{entry.taskTitle}</p>
        </div>
        <p className="mt-0.5 text-[0.75rem] text-subtle">{entry.projectName}</p>
        {entry.notes ? (
          <p className="mt-1.5 max-w-prose text-[0.8125rem] leading-relaxed text-muted">
            {entry.notes}
          </p>
        ) : null}

        <label className="mt-2.5 block">
          <span className="sr-only">Review note for {entry.taskTitle}</span>
          <input
            type="text"
            placeholder="Add a note to this decision (optional)"
            maxLength={5000}
            value={draft.reviewNote}
            disabled={busy || !canApprove}
            onChange={(event) => onChange({ reviewNote: event.target.value })}
            className={cn(CONTROL_CLASS, 'h-9 max-w-md border-line-strong text-[0.8125rem]')}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-4 lg:justify-end">
        <div>
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-subtle">
            Logged
          </p>
          <p className="mt-1 font-mono text-[1.0625rem] tnum">{formatHours(entry.minutes)}</p>
        </div>

        <div>
          <label
            className="block text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-subtle"
            htmlFor={`billable-${entry.id}`}
          >
            Billable
          </label>
          <div className="mt-1 flex items-stretch gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              aria-label="Fifteen minutes less billable"
              disabled={busy || !canApprove || draft.billableMinutes <= BILLABLE_MIN}
              onClick={() => step(-MINUTE_STEP)}
              className="w-8 px-0 font-mono"
            >
              &minus;
            </Button>
            <input
              id={`billable-${entry.id}`}
              type="number"
              inputMode="numeric"
              step={MINUTE_STEP}
              min={BILLABLE_MIN}
              max={BILLABLE_MAX}
              value={draft.billableMinutes}
              disabled={busy || !canApprove}
              onChange={(event) => {
                const next = Number.parseInt(event.target.value, 10);
                onChange({ billableMinutes: Number.isNaN(next) ? 0 : next });
              }}
              className={cn(
                CONTROL_CLASS,
                'h-9 w-[4.5rem] text-center font-mono text-[0.8125rem] tnum',
                diverged ? 'border-warn bg-warn-soft/60' : 'border-line-strong',
              )}
            />
            <Button
              size="sm"
              variant="secondary"
              aria-label="Fifteen minutes more billable"
              disabled={busy || !canApprove || draft.billableMinutes >= BILLABLE_MAX}
              onClick={() => step(MINUTE_STEP)}
              className="w-8 px-0 font-mono"
            >
              +
            </Button>
          </div>
          <p
            className={cn(
              'mt-1 text-[0.75rem] tnum',
              diverged ? 'font-medium text-warn-ink' : 'text-subtle',
            )}
          >
            {formatHours(draft.billableMinutes)}
            {diverged ? ` · ${deltaHint(draft.billableMinutes - entry.minutes)}` : ''}
          </p>
        </div>

        {canApprove ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              iconLeft="close"
              onClick={() => onDecide('rejected')}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              iconLeft="check"
              onClick={() => onDecide('approved')}
            >
              Approve
            </Button>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-[0.8125rem] text-subtle">
            <Icon name="info" size={13} />
            Read only
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Grouping and totals                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Person plus week, keyed on the Monday the work date falls in.
 *
 * `weekStartOf` is the shared calendar helper: doing this with millisecond
 * arithmetic lands on the wrong Monday twice a year, and the resulting group
 * would silently split one week across two cards.
 */
function groupByPersonAndWeek(entries: readonly TimeEntryDto[]): Group[] {
  const groups = new Map<string, Group>();

  for (const entry of entries) {
    const weekStart = weekStartOf(entry.workDate);
    const key = `${entry.userId}|${weekStart}`;
    const existing = groups.get(key);
    if (existing) {
      existing.entries.push(entry);
    } else {
      groups.set(key, {
        key,
        userId: entry.userId,
        userName: entry.userName,
        weekStart,
        entries: [entry],
      });
    }
  }

  for (const group of groups.values()) {
    group.entries.sort((a, b) => a.workDate.localeCompare(b.workDate));
  }

  // Most recent week first, then alphabetically - so the newest decisions are
  // at the top and the same person's weeks stay adjacent.
  return [...groups.values()].sort(
    (a, b) => b.weekStart.localeCompare(a.weekStart) || a.userName.localeCompare(b.userName),
  );
}

function totalsFor(
  entries: readonly TimeEntryDto[],
  drafts: Record<string, Draft>,
): { logged: number; billable: number } {
  let logged = 0;
  let billable = 0;
  for (const entry of entries) {
    logged += entry.minutes;
    billable += drafts[entry.id]?.billableMinutes ?? entry.minutes;
  }
  return { logged, billable };
}

/** `+0.50h billed` / `-1.00h billed`. Empty when there is no difference. */
function deltaHint(deltaMinutes: number): string {
  if (deltaMinutes === 0) return 'billed as logged';
  const sign = deltaMinutes > 0 ? '+' : '−';
  return `${sign}${formatHours(Math.abs(deltaMinutes))} billed`;
}
